// THE SPINNER, IN TWO PLACES (owner, 2026-10-02: "show a spinner on the
// top pannel next to review and when it it is from source, show the
// spinenr next to source").
//
// IT IS NOT ONLY A SPINNER, and that is the part worth reading. A spinner
// says "something is happening" and says nothing about the three states
// that actually matter on a six-minute job:
//
//   running   N of M, so a person can tell progress from a hang. A bare
//             spinner on a job that died looks identical to one that is
//             working, and "forever busy" is the exact failure this whole
//             move was meant to remove from the apply path.
//   stopped   the run's heartbeat went cold: the heavy lane SIGKILLed it
//             on its deadline, or the process aborted and ran no finally.
//             It SAYS so, with the part it stopped on.
//   failed    the reason, in words, from the part that stopped the group -
//             the 16 MB ceiling reads as "too large to store, needs
//             splitting into more parts", not as a buffer offset.
//
// A finished run renders NOTHING. The page reloads its numbers when a run
// completes; a green tick that lingers next to a button is furniture.
import React from "react";
import { Spin, Tooltip } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";

const SOFT = "#6B7280";
const BAD = "#CF1322";

// THE SENTENCES, AS FUNCTIONS, so they can be tested without hovering.
//
// antd renders a Tooltip's title into a portal on hover, so it is not in the
// DOM for a test to read - the existing test could only assert a NEGATIVE
// about it. These are pure, and the component below is their only caller.
//
// AN IMPORT IS NOT AN APPLY. The one sentence served both, so a failed
// IMPORT read "19 of 20 parts were applied; the rest are untouched and can
// be applied again" (owner, 2026-10-04) - naming an operation that did not
// happen, and offering a remedy that was not available. It also promised the
// written parts survived, which this cannot see: his had been cleared
// minutes earlier.
export function runWords(run) {
  const r = run || {};
  const isImport = r.origin === "source";
  return {
    isImport,
    of: r.partsInGroup || 0,
    done: r.partsDone || 0,
    verb: isImport ? "imported" : "applied",
    noun: isImport ? "import" : "apply",
    remedy: isImport
      ? "Import again to get the rest."
      : "The rest are untouched and can be applied again.",
  };
}

export function stoppedSentence(run) {
  const { isImport, of, done, verb, remedy } = runWords(run);
  const r = run || {};
  return `${isImport ? "The import" : "The apply"} of group ${r.groupId} stopped without finishing`
    + `${r.currentPart ? ` (last part: ${r.currentPart})` : ""}. `
    + `${done} of ${of || "?"} parts were ${verb}. ${remedy}`;
}

export function failedSentence(run) {
  const { isImport, of, done, verb, remedy } = runWords(run);
  const r = run || {};
  return `${r.error || (isImport ? "The import failed." : "The apply failed.")} `
    + `${done} of ${of || "?"} parts were ${verb}. ${remedy}`;
}

export default function ApplySpinner({ run, where, unreachable = false }) {
  // LOST TOUCH IS ITS OWN STATE, and it is the one the owner met: my
  // restart dropped one poll, the hook stopped asking, and the spinner sat
  // on the word "queued" for six minutes while the job finished 7 of 7
  // behind it. A spinner that cannot see the server must say so rather
  // than keep turning on the last thing it heard.
  if (unreachable && run && (run.live || run.status === 'queued')) {
    return (
      <Tooltip title={
        `Lost touch with the server while following this apply, so what is shown may be out of date. `
        + `The job itself is unaffected - it runs on the worker. Reload the page to pick it up again.`
      }>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11.5, color: BAD, whiteSpace: "nowrap" }}>
          <ExclamationCircleOutlined /> not updating
        </span>
      </Tooltip>
    );
  }
  if (!run) return null;
  // Next to Source only when the work came FROM a source; next to Review
  // always. That is the owner's rule, and it is why `where` exists.
  if (where === "source" && run.origin !== "source") return null;

  // A PROGRESS HINT MAY APPEAR TWICE; A FAILURE MAY NOT.
  //
  // Owner, 2026-10-04: "why i have 2 failed workings on the top bar" - with
  // the same sentence in both. There was ONE run row; the rule above shows a
  // source run in BOTH places, which is reasonable while it is spinning
  // (either spot tells you work is happening) and wrong once it has
  // stopped. Two identical failures read as two failures.
  //
  // So a dead run appears ONCE, and next to REVIEW - which is the spot the
  // rule above shows ALWAYS. My first attempt put a source failure next to
  // Source instead, "where it came from", and two existing tests failed and
  // were right to: Source is the conditional spot, so a failure parked there
  // is a failure somebody can fail to see. The reliable place wins.
  const dead = run.stale || run.status === "failed";
  if (dead && where !== "review") return null;

  const of = run.partsInGroup || 0;
  const done = run.partsDone || 0;
  const by = run.by ? ` · started by ${run.by}` : "";
  // AN IMPORT IS NOT AN APPLY, and the message used the apply's words for
  // both. "19 of 20 parts were applied; the rest are untouched and can be
  // applied again" was shown for a failed IMPORT, where nothing was applied,
  // the remedy is to import again, and - because the owner had just had the
  // dead parts cleared - the parts it promised were "untouched" no longer
  // existed. A sentence that names the wrong operation and a remedy that is
  // not available is worse than a bare "failed".


  if (run.live) {
    const label = of ? `${Math.min(done + 1, of)} of ${of}` : "running";
    const queued = run.status === "queued";
    return (
      <Tooltip title={
        queued
          ? `Apply queued for group ${run.groupId}${by}. The worker picks it up within a few seconds.`
          : `Applying group ${run.groupId}: part ${label}${run.currentPart ? ` (last: ${run.currentPart})` : ""}${by}`
          + `${run.lane === "inline" ? " — running in this process, the worker is not up" : ""}`
      }>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11.5, color: SOFT, whiteSpace: "nowrap" }}>
          <Spin size="small" />
          {queued ? "queued" : label}
        </span>
      </Tooltip>
    );
  }

  // A JOB THAT WAS KILLED. Not spinning, and not silent either: the parts
  // it finished are applied and the rest can be applied again.
  if (run.stale) {
    return (
      <Tooltip title={
stoppedSentence(run)
      }>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11.5, color: BAD, whiteSpace: "nowrap" }}>
          <ExclamationCircleOutlined /> stopped
        </span>
      </Tooltip>
    );
  }

  if (run.status === "failed") {
    return (
      <Tooltip title={
failedSentence(run)
      }>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11.5, color: BAD, whiteSpace: "nowrap" }}>
          <ExclamationCircleOutlined /> failed
        </span>
      </Tooltip>
    );
  }

  return null;
}
